import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Target, Users, MessageCircle, Send, Loader2, ChevronRight, ExternalLink, X, Save, Check, Crosshair, Brain, Sprout } from "lucide-react";
import FeedbackButtons from "@/components/FeedbackButtons";

const AgentActionsCenter = ({ userId, onRefresh }) => {
  const [huntProcessing, setHuntProcessing] = useState(false);
  const [farmProcessing, setFarmProcessing] = useState(false);
  const [advisorProcessing, setAdvisorProcessing] = useState(false);
  const [advisorMode, setAdvisorMode] = useState(false);
  const [advisorQuestion, setAdvisorQuestion] = useState("");
  const [advisorMessages, setAdvisorMessages] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showAdvisorWelcome, setShowAdvisorWelcome] = useState(false);
  const [savedLeads, setSavedLeads] = useState(new Set());
  const [savingLead, setSavingLead] = useState(null);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem(`advisor-welcome-${userId}`);
  }, [userId]);

  const parseMarkdownContent = (content) => {
    if (!content) return [];
    
    const sections = [];
    const lines = content.split('\n');
    let currentLead = null;
    let currentSection = null;
    let currentContent = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      
      if (trimmed.match(/^\*\*(LEAD|OPPORTUNITY) \d+\*\*$/i)) {
        if (currentLead && currentSection) {
          currentSection.content = currentContent.join('\n');
          currentLead.subsections.push(currentSection);
        }
        if (currentLead) sections.push(currentLead);
        
        currentLead = {
          title: trimmed.replace(/\*\*/g, ''),
          subsections: []
        };
        currentSection = null;
        currentContent = [];
      }
      else if (trimmed.match(/^\*\*[A-Z\s]+:?\*\*$/i)) {
        if (currentSection) {
          currentSection.content = currentContent.join('\n');
          currentLead.subsections.push(currentSection);
        }
        currentSection = {
          title: trimmed.replace(/\*\*/g, '').replace(/:$/, ''),
          content: ''
        };
        currentContent = [];
      }
      else if (trimmed && currentSection) {
        currentContent.push(trimmed);
      }
    });

    if (currentSection) {
      currentSection.content = currentContent.join('\n');
      currentLead?.subsections.push(currentSection);
    }
    if (currentLead) sections.push(currentLead);

    return sections;
  };

  const formatContent = (text) => {
    if (!text) return text;
    
    const parts = [];
    const combinedRegex = /(\*\*[^*]+\*\*)|(https?:\/\/[^\s]+)/g;
    let lastIndex = 0;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      if (match[1]) {
        const boldText = match[1].replace(/\*\*/g, '');
        // Remove bold styling - render as normal text
        parts.push(
          <span key={`b-${match.index}`} className="text-gray-200">
            {boldText}
          </span>
        );
      } else if (match[2]) {
        const url = match[2];
        parts.push(
          <a
            key={`l-${match.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-neon-cyan hover:text-neon-cyan/80 underline transition-colors inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {url.length > 60 ? url.substring(0, 60) + '...' : url}
            <ExternalLink className="h-3 w-3 inline" />
          </a>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const renderBulletContent = (content) => {
    const lines = content.split('\n');
    const items = [];
    let currentParagraph = [];

    lines.forEach((line, idx) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('•')) {
        if (currentParagraph.length > 0) {
          items.push(
            <p key={`p-${idx}`} className="text-gray-300 leading-relaxed mb-2">
              {formatContent(currentParagraph.join(' '))}
            </p>
          );
          currentParagraph = [];
        }
        items.push(
          <li key={`li-${idx}`} className="text-gray-300 leading-relaxed">
            {formatContent(trimmed.substring(1).trim())}
          </li>
        );
      } else if (trimmed) {
        currentParagraph.push(trimmed);
      } else if (currentParagraph.length > 0) {
        items.push(
          <p key={`p-${idx}`} className="text-gray-300 leading-relaxed mb-2">
            {formatContent(currentParagraph.join(' '))}
          </p>
        );
        currentParagraph = [];
      }
    });

    if (currentParagraph.length > 0) {
      items.push(
        <p key="final" className="text-gray-300 leading-relaxed mb-2">
          {formatContent(currentParagraph.join(' '))}
        </p>
      );
    }

    return items;
  };

  const renderContentWithLinks = (content) => {
    const contentItems = renderBulletContent(content);
    
    return (
      <div>
        <ul className="list-none space-y-2">
          {contentItems}
        </ul>
      </div>
    );
  };

  const handleHunt = async () => {
    setHuntProcessing(true);
    setShowResults(false);
    setSavedLeads(new Set());
    try {
      const response = await axios.post(`${API}/agent/hunt/${userId}`);
      
      setResults({
        mode: "Hunt",
        data: response.data,
        parsed: parseMarkdownContent(response.data.insights || "")
      });
      setShowResults(true);
      
      toast.success("Hunt Mode: 3 high-quality leads generated!");
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Hunt mode failed");
    } finally {
      setHuntProcessing(false);
    }
  };

  const extractLeadInfo = (lead) => {
    const info = {
      companyName: "",
      contactName: "",
      contactTitle: "",
      linkedinUrl: "",
      website: "",
      industry: "",
      companySize: "",
      notes: ""
    };

    lead.subsections?.forEach(section => {
      const content = section.content || "";
      const title = section.title?.toLowerCase() || "";
      
      if (title.includes("company")) {
        const nameMatch = content.match(/\*\*Name:\*\*\s*(.+?)(?:\n|$)/i) || 
                         content.match(/Name:\s*(.+?)(?:\n|$)/i);
        if (nameMatch) info.companyName = nameMatch[1].trim();
        
        const urlMatch = content.match(/https?:\/\/[^\s\)]+/);
        if (urlMatch) info.website = urlMatch[0];
      }
      
      if (title.includes("decision") || title.includes("target") || title.includes("persona")) {
        const titleMatch = content.match(/\*\*(?:Target )?Title:\*\*\s*(.+?)(?:\n|$)/i) ||
                          content.match(/Title:\s*(.+?)(?:\n|$)/i);
        if (titleMatch) info.contactTitle = titleMatch[1].trim();
        
        const linkedinMatch = content.match(/https?:\/\/(?:www\.)?linkedin\.com\/[^\s\)]+/);
        if (linkedinMatch) info.linkedinUrl = linkedinMatch[0];
      }
      
      info.notes += `${section.title}: ${content}\n\n`;
    });

    if (!info.companyName && lead.title) {
      info.companyName = lead.title.replace(/lead\s*\d+/i, '').trim() || `Lead from ${results?.mode || 'Hunt'}`;
    }

    return info;
  };

  const handleSaveLead = async (lead, leadIndex) => {
    setSavingLead(leadIndex);
    try {
      const leadInfo = extractLeadInfo(lead);
      
      const leadData = {
        userId: userId,
        companyName: leadInfo.companyName || `Lead ${leadIndex + 1}`,
        contactName: leadInfo.contactName || null,
        contactTitle: leadInfo.contactTitle || null,
        linkedinUrl: leadInfo.linkedinUrl || null,
        website: leadInfo.website || null,
        industry: leadInfo.industry || "Consulting",
        companySize: leadInfo.companySize || null,
        source: results?.mode?.toLowerCase() || "hunt",
        sourceUrl: leadInfo.website || leadInfo.linkedinUrl || null,
        notes: leadInfo.notes?.substring(0, 2000) || null
      };

      await axios.post(`${API}/leads`, leadData);
      
      setSavedLeads(prev => new Set([...prev, leadIndex]));
      toast.success(`Lead "${leadData.companyName}" saved to CRM!`);
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error("Failed to save lead");
    } finally {
      setSavingLead(null);
    }
  };

  const handleFarm = async () => {
    setFarmProcessing(true);
    setShowResults(false);
    try {
      const response = await axios.post(`${API}/agent/farm/${userId}`);
      
      if (response.data.needsAccountData) {
        toast.info(response.data.message);
        setResults({
          mode: "Farm",
          needsAccounts: true,
          message: response.data.insights
        });
        setShowResults(true);
      } else {
        setResults({
          mode: "Farm",
          data: response.data,
          parsed: parseMarkdownContent(response.data.insights || "")
        });
        setShowResults(true);
        toast.success("Farm Mode: 3 expansion opportunities identified!");
      }
      
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Farm mode failed");
    } finally {
      setFarmProcessing(false);
    }
  };

  const handleAdvisorToggle = async () => {
    if (!advisorMode) {
      setAdvisorMode(true);
      setShowResults(false);
      
      const hasSeenWelcome = localStorage.getItem(`advisor-welcome-${userId}`);
      if (!hasSeenWelcome) {
        setShowAdvisorWelcome(true);
      }
      
      try {
        const response = await axios.post(`${API}/agent/advisor/${userId}`);
        setAdvisorMessages([{
          type: "advisor",
          content: response.data.response
        }]);
      } catch (error) {
        console.error("Error:", error);
      }
    } else {
      setAdvisorMode(false);
      setAdvisorMessages([]);
      setAdvisorQuestion("");
    }
  };

  const handleAdvisorWelcomeClose = () => {
    setShowAdvisorWelcome(false);
    localStorage.setItem(`advisor-welcome-${userId}`, 'true');
  };

  const handleAdvisorQuestion = async () => {
    if (!advisorQuestion.trim()) {
      toast.error("Please enter a question");
      return;
    }

    const userMessage = advisorQuestion;
    setAdvisorMessages(prev => [...prev, {
      type: "user",
      content: userMessage
    }]);
    setAdvisorQuestion("");
    setAdvisorProcessing(true);

    try {
      const response = await axios.post(`${API}/agent/advisor/${userId}`, null, {
        params: { question: userMessage }
      });
      
      setAdvisorMessages(prev => [...prev, {
        type: "advisor",
        content: response.data.response
      }]);
      
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Advisor failed to respond");
    } finally {
      setAdvisorProcessing(false);
    }
  };

  const renderFormattedContent = () => {
    if (!results || !results.parsed || results.parsed.length === 0) {
      return (
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 whitespace-pre-line leading-relaxed">
            {results?.data?.insights || "Processing results..."}
          </p>
          {results?.data?.actionId && (
            <motion.div 
              className="mt-6 p-4 glass-card-light rounded-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-white font-medium">Rate these results</span>
                  <p className="text-xs text-gray-500">Help us improve future outputs</p>
                </div>
                <FeedbackButtons
                  userId={userId}
                  agentType={results?.mode?.toLowerCase() || "unknown"}
                  actionId={results?.data?.actionId}
                  outputSummary={results?.data?.insights?.substring(0, 200)}
                  size="default"
                />
              </div>
            </motion.div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {results.parsed.map((lead, idx) => (
          <motion.div 
            key={idx} 
            className="glass-card-light rounded-xl p-6 hover-glow-cyan"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            {/* Lead Title with Feedback and Save Buttons */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <motion.div 
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center border border-neon-cyan/20"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <span className="text-neon-cyan">{idx + 1}</span>
                </motion.div>
                <h3 className="text-xl text-white tracking-tight">{lead.title || `Lead ${idx + 1}`}</h3>
              </div>
              
              <div className="flex items-center gap-3">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={() => handleSaveLead(lead, idx)}
                    disabled={savingLead === idx || savedLeads.has(idx)}
                    size="sm"
                    className={`${
                      savedLeads.has(idx) 
                        ? 'bg-neon-green/20 border-neon-green/30 text-neon-green cursor-default' 
                        : 'bg-neon-pink hover:bg-neon-pink/90 border-0'
                    } text-white rounded-lg`}
                    data-testid={`save-lead-btn-${idx}`}
                  >
                    {savingLead === idx ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : savedLeads.has(idx) ? (
                      <Check className="h-4 w-4 mr-1" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    {savedLeads.has(idx) ? 'Saved' : 'Save Lead'}
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Subsections */}
            {lead.subsections && lead.subsections.map((section, sIdx) => (
              <motion.div 
                key={sIdx} 
                className="mb-6 last:mb-0"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * sIdx }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ChevronRight className="h-4 w-4 text-neon-cyan" />
                  <h4 className="text-sm text-neon-cyan/80 uppercase tracking-wider">
                    {section.title}
                  </h4>
                </div>
                <div className="pl-6">
                  {renderContentWithLinks(section.content)}
                </div>
              </motion.div>
            ))}
            
            {/* Feedback Section */}
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-xs text-gray-500">Was this lead helpful?</span>
              <FeedbackButtons
                userId={userId}
                agentType="hunt"
                actionId={results?.data?.actionId}
                outputSummary={lead.title}
                context={{ leadIndex: idx, mode: "hunt" }}
              />
            </div>
          </motion.div>
        ))}
        
        {/* Overall Feedback */}
        <motion.div 
          className="glass-card-light rounded-xl p-4 flex items-center justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div>
            <span className="text-sm text-white font-medium">Rate these hunt results overall</span>
            <p className="text-xs text-gray-500">Your feedback improves future searches</p>
          </div>
          <FeedbackButtons
            userId={userId}
            agentType="hunt"
            actionId={results?.data?.actionId}
            outputSummary={`Hunt: ${results.parsed.length} leads`}
            context={{ mode: "hunt", totalLeads: results.parsed.length }}
            size="default"
          />
        </motion.div>
      </div>
    );
  };

  // Action button component for consistent styling
  const ActionButton = ({ onClick, disabled, processing, icon: Icon, color, title, subtitle, testId }) => {
    const colorStyles = {
      green: {
        bg: "from-neon-green/20 to-neon-green/5",
        border: "border-neon-green/30",
        text: "text-neon-green",
        iconBg: "bg-neon-green/20"
      },
      blue: {
        bg: "from-blue-500/20 to-blue-500/5",
        border: "border-blue-500/30",
        text: "text-blue-400",
        iconBg: "bg-blue-500/20"
      },
      purple: {
        bg: "from-purple-500/20 to-purple-500/5",
        border: "border-purple-500/30",
        text: "text-purple-400",
        iconBg: "bg-purple-500/20"
      }
    };

    const style = colorStyles[color];

    return (
      <motion.button
        onClick={onClick}
        disabled={disabled}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        className={`w-full bg-gradient-to-br ${style.bg} border ${style.border} rounded-xl p-5 flex items-center justify-between transition-all disabled:opacity-50 disabled:cursor-not-allowed group`}
        data-testid={testId}
      >
        <div className="flex items-center gap-4">
          <motion.div 
            className={`p-3 rounded-xl ${style.iconBg} group-hover:scale-110 transition-transform`}
            animate={processing ? { rotate: 360 } : {}}
            transition={processing ? { duration: 1, repeat: Infinity, ease: "linear" } : {}}
          >
            <Icon className={`h-6 w-6 ${style.text}`} />
          </motion.div>
          <div className="text-left">
            <div className={`${style.text} text-lg`}>{title}</div>
            <div className="text-xs text-gray-500">{subtitle}</div>
          </div>
        </div>
        {processing && <Loader2 className={`h-5 w-5 animate-spin ${style.text}`} />}
      </motion.button>
    );
  };

  return (
    <>
      <motion.div 
        className="glass-card p-6 h-[600px] flex flex-col relative overflow-hidden"
        data-testid="agent-actions-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none" />

        {/* Header */}
        <div className="mb-6 relative z-10">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-cyan/10 border border-neon-cyan/20">
              <Crosshair className="h-6 w-6 text-neon-cyan" />
            </div>
            <div>
              <h2 className="text-xl tracking-tight">
                <span className="text-neon-cyan">Agent Actions</span>
              </h2>
              <p className="text-sm text-gray-500">Self-Service Sales Intelligence</p>
            </div>
          </div>
        </div>

        {!advisorMode && !showResults && (
          <>
            {/* Action Buttons */}
            <div className="space-y-4 mb-6 relative z-10">
              <ActionButton
                onClick={handleHunt}
                disabled={huntProcessing}
                processing={huntProcessing}
                icon={Crosshair}
                color="green"
                title="Hunt"
                subtitle="Generate 3 high-quality leads with AI"
                testId="hunt-btn"
              />

              <ActionButton
                onClick={handleFarm}
                disabled={farmProcessing}
                processing={farmProcessing}
                icon={Sprout}
                color="blue"
                title="Farm"
                subtitle="Find expansion opportunities in accounts"
                testId="farm-btn"
              />

              <ActionButton
                onClick={handleAdvisorToggle}
                disabled={advisorProcessing}
                processing={advisorProcessing}
                icon={Brain}
                color="purple"
                title="Advisor"
                subtitle="Chat with sales strategy expert"
                testId="advisor-btn"
              />
            </div>

            {/* Info Box */}
            <motion.div 
              className="glass-card-light rounded-xl p-4 mt-auto relative z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-sm text-gray-400 leading-relaxed">
                Click any action to activate <span className="text-neon-cyan">AI-powered</span> sales intelligence. Results will appear here with real-time web data.
              </p>
            </motion.div>
          </>
        )}

        {showResults && !advisorMode && results && (
          <div className="flex-1 flex flex-col overflow-hidden relative z-10">
            <div className="mb-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg text-neon-cyan/80">{results.mode} Results</h3>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setShowResults(false)}
                  size="sm"
                  className="bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 rounded-lg"
                >
                  Back
                </Button>
              </motion.div>
            </div>
            
            <ScrollArea className="flex-1 pr-4">
              {results.needsAccounts ? (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-white/10 inline-block mb-4">
                    <Users className="h-12 w-12 text-blue-400" />
                  </div>
                  <p className="text-gray-400 mb-4">{results.message}</p>
                  <Button
                    onClick={() => toast.info("Navigate to Accounts to add your accounts")}
                    size="sm"
                    className="bg-gradient-to-r from-neon-cyan to-neon-cyan/80 hover:from-neon-cyan/90 hover:to-neon-cyan/70 text-black font-semibold rounded-lg"
                  >
                    Go to Accounts
                  </Button>
                </motion.div>
              ) : (
                renderFormattedContent()
              )}
            </ScrollArea>
          </div>
        )}

        {advisorMode && (
          <div className="flex-1 flex flex-col relative z-10">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg text-purple-300">Sales Strategy Advisor</h3>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleAdvisorToggle}
                  size="sm"
                  className="bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 rounded-lg"
                >
                  Close
                </Button>
              </motion.div>
            </div>
            
            <ScrollArea className="flex-1 pr-4 mb-4">
              <div className="space-y-4">
                <AnimatePresence>
                  {advisorMessages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`${
                        msg.type === "user"
                          ? "glass-card-light border-neon-cyan/20 ml-8"
                          : "glass-card-light border-purple-500/20 mr-8"
                      } border rounded-xl p-4`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-xs font-medium ${msg.type === "user" ? "text-neon-cyan" : "text-purple-400"}`}>
                          {msg.type === "user" ? "You" : "Advisor"}
                        </div>
                        {msg.type === "advisor" && idx > 0 && (
                          <FeedbackButtons
                            userId={userId}
                            agentType="advisor"
                            outputSummary={msg.content?.substring(0, 200)}
                            context={{ messageIndex: idx }}
                          />
                        )}
                      </div>
                      <div className="text-sm text-gray-300 leading-relaxed">
                        {renderBulletContent(msg.content)}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {advisorProcessing && (
                  <motion.div 
                    className="flex items-center gap-2 text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                    <span className="text-sm">Advisor is thinking...</span>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            <div className="flex gap-2">
              <Input
                value={advisorQuestion}
                onChange={(e) => setAdvisorQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdvisorQuestion()}
                placeholder="Ask about sales strategy, deals, accounts..."
                className="bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 rounded-lg focus:border-purple-400 transition-colors"
                disabled={advisorProcessing}
              />
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleAdvisorQuestion}
                  disabled={advisorProcessing || !advisorQuestion.trim()}
                  className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-lg px-4"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </motion.div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Advisor Welcome Modal */}
      <AnimatePresence>
        {showAdvisorWelcome && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80" onClick={handleAdvisorWelcomeClose} />
            <motion.div 
              className="glass-card p-8 max-w-lg mx-4 relative z-10 holo-border"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <button
                onClick={handleAdvisorWelcomeClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              
              <div className="mb-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600/20 to-pink-500/20 flex items-center justify-center mx-auto mb-4 border border-purple-500/20">
                  <Brain className="h-10 w-10 text-purple-400" />
                </div>
                <h2 className="text-2xl text-center mb-2">
                  <span className="text-purple-300">Sales Strategy Advisor</span>
                </h2>
                <p className="text-gray-400 text-center">
                  Your AI expert using Forward Deployed Selling
                </p>
              </div>

              <div className="space-y-3 mb-6 text-gray-300">
                <p className="leading-relaxed">
                  <span className="text-white">I specialize in:</span>
                </p>
                <ul className="list-none space-y-2 pl-4">
                  {["Account strategy and planning", "Deal acceleration tactics", "Objection handling", "Persona Insights", "Consultative selling", "Revenue generation strategies"].map((item, i) => (
                    <motion.li 
                      key={i}
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
                <p className="text-sm text-gray-500 pt-3">
                  Ask me anything about your sales challenges, and I'll provide guidance rooted in proven methodologies.
                </p>
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={handleAdvisorWelcomeClose}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white font-semibold py-3 rounded-xl"
                >
                  Got it, let's start!
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AgentActionsCenter;
